import { resetUserFetch } from '@/api/user/userApi';
import { useAuthStore } from '@/store/authStore';
import { useTournamentStore } from '@/store/useTournamentStore';

/**
 * Identity of the session currently installed in the auth store: the account
 * being impersonated (and whether control was taken), the anonymous "nobody is
 * signed in" state, or somebody's own session.
 *
 * Taking control counts as a switch even though the account stays the same: the
 * read-only token may carry an OWNER role the backend lends only for viewing,
 * and the takeover token runs with the account's real roles. Whatever was
 * fetched under the lent role (the sidebar's tournaments, a `GET /users` still
 * in flight) must not leak into the session that can write.
 *
 * Deliberately NOT the `accessToken` itself. That value also changes on an
 * ordinary refresh, which is the same user and must not cost them their caches.
 * Whether there is a token at all is a different question: it only flips when a
 * session ends or begins, which is exactly what has to clear them. Without that
 * half, an ordinary logout/login goes `null` -> `null` on the impersonation key
 * and returns early — so a `GET /users` still in flight when A logs out is
 * still parked in the single-flight when B logs in, and `Login` installs A's
 * profile, roles and landing route into B's session.
 */
const sessionKey = (state: {
  impersonation: { actingAs: { pk: string }; takeover: boolean } | null;
  accessToken: string | null;
}) => {
  if (state.impersonation) {
    const { actingAs, takeover } = state.impersonation;
    return `acting-as:${actingAs.pk}:${takeover ? 'control' : 'read'}`;
  }
  return state.accessToken ? 'own' : 'signed-out';
};

/**
 * Everything cached per session that lives outside `authStore` and therefore
 * does not get switched with it.
 *
 * `useTournamentStore` latches on `isInitialized`, so without this an exited
 * support session leaves the previous owner's tournaments in the sidebar — with
 * links to their tournament ids — for the next account that walks in. The user
 * single-flight in `userApi` is the same defect wearing another hat: a
 * `GET /users` still in flight at the moment of the switch resolves into the
 * session that replaced it.
 *
 * Add any future session-scoped cache here rather than at the call sites: the
 * transitions are spread across `startImpersonation`, `stopImpersonation`, the
 * 401 branch in `lib/api.ts` and `updateTokens`, and they will not each
 * remember a new entry.
 */
const clearSessionScopedCaches = () => {
  resetUserFetch();
  useTournamentStore.getState().clearTournaments();
};

/**
 * Wires the caches above to the auth store. Imported once at app start
 * (`main.tsx`), never from a store or an api module: `userApi` already imports
 * `authStore`, so the dependency has to point this way to stay acyclic.
 *
 * Returns the unsubscribe so a test can tear it down.
 */
export const watchSessionSwitches = () =>
  useAuthStore.subscribe((state, previousState) => {
    if (sessionKey(state) === sessionKey(previousState)) {
      return;
    }
    clearSessionScopedCaches();
  });
